import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Alert, PageHeader } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

export default function AdminSettings() {
  const { user } = useAuthStore();
  const [pwd, setPwd] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const [saving, setSaving] = useState(false);

  const changePassword = async () => {
    setPwdError('');
    if (!pwd.current || !pwd.newPwd || !pwd.confirm) return setPwdError('All fields required');
    if (pwd.newPwd.length < 8) return setPwdError('New password must be at least 8 characters');
    if (pwd.newPwd !== pwd.confirm) return setPwdError('Passwords do not match');
    setSaving(true);
    try {
      await api.post('/users/change-password', { currentPassword: pwd.current, newPassword: pwd.newPwd });
      toast.success('Password updated!');
      setPwd({ current: '', newPwd: '', confirm: '' });
    } catch (e) { setPwdError(e.response?.data?.message || 'Failed. Check your current password.'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Admin Settings" subtitle="Manage your admin account" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Account Info */}
        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 20 }}>👤 Account Information</h3>
          {[
            ['Name', user?.name],
            ['Email', user?.email],
            ['Role', 'Super Admin'],
            ['Last Login', user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : '—'],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
              <span style={{ color: '#666' }}>{l}</span>
              <span style={{ color: '#ccc', fontWeight: 500 }}>{v}</span>
            </div>
          ))}
        </Card>

        {/* Change Password */}
        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 20 }}>🔐 Change Password</h3>
          {pwdError && <Alert type="danger" style={{ marginBottom: 16 }}>{pwdError}</Alert>}
          <Input label="Current Password" type="password" placeholder="Enter current password" value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))} />
          <Input label="New Password" type="password" placeholder="Min. 8 characters" value={pwd.newPwd} onChange={e => setPwd(p => ({ ...p, newPwd: e.target.value }))} />
          <Input label="Confirm New Password" type="password" placeholder="Repeat new password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} />
          <Button loading={saving} onClick={changePassword} fullWidth>Update Password</Button>
        </Card>

        {/* Platform Info */}
        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 20 }}>ℹ️ Platform Info</h3>
          {[
            ['Platform', 'EmailMark v2'],
            ['By', 'MS Discoveries'],
            ['Support', 'support@msdiscoveries.com'],
            ['Phone', '+91 9443724724'],
            ['Hours', 'Mon–Sat, 10AM–6PM'],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
              <span style={{ color: '#666' }}>{l}</span>
              <span style={{ color: '#ccc' }}>{v}</span>
            </div>
          ))}
        </Card>

        {/* Password Reset Requests */}
        <PasswordResetRequests />
      </div>
    </div>
  );
}

function PasswordResetRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try { const r = await api.get('/superadmin/password-reset-requests'); setRequests(r.data.requests || []); }
    catch {} finally { setLoading(false); }
  };

  return (
    <Card>
      <h3 style={{ fontWeight: 700, marginBottom: 16 }}>🔑 Password Reset Requests</h3>
      <p style={{ fontSize: 12, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
        Clients who requested a password reset. Go to <strong style={{ color: '#ccc' }}>Client Management</strong> to reset their password and notify them.
      </p>
      {loading ? <div style={{ color:'#555', fontSize: 13 }}>Loading...</div> : (
        requests.length === 0 ? (
          <div style={{ textAlign:'center', padding: '20px 0', color:'#444', fontSize: 13 }}>No pending requests</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {requests.slice(0, 5).map(r => (
              <div key={r.id} style={{ background: '#1a1a1a', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(243,156,18,0.2)' }}>
                <div style={{ fontSize: 13, color: '#F39C12', fontWeight: 600 }}>📧 {r.details?.match(/\S+@\S+/)?.[0] || 'Unknown'}</div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 3 }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</div>
              </div>
            ))}
          </div>
        )
      )}
    </Card>
  );
}
